import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as Logo } from "./images/logo.svg";
import "./App.css";
import TextInputField from "./components/TextInputField";
import StarRating from "./components/StarRating";

// URL parameters that can be used instead of a token (for QR codes)
interface DirectParams {
  recordId?: string;
  feedbackType: string;
}

interface FeedbackPayload {
  feedbackRating: number;
  feedbackText: string;
  token?: string;
  recordId?: string;
  feedbackType?: string;
  email?: string;
  feedbackId?: string;
}

type RatingStatus = 'idle' | 'saving' | 'saved' | 'error';

// LocalStorage caching for spam prevention
interface CachedFeedback {
  feedbackId: string;
  rating: number;
  timestamp: number;
}

const CACHE_KEY_PREFIX = 'rw1_feedback_';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function getCacheKey(token: string | null, recordId: string | null, feedbackType: string | null): string | null {
  if (token) {
    // Use first 32 chars of token to create a stable key (avoid URL-length issues)
    return `${CACHE_KEY_PREFIX}token_${token.substring(0, 32)}`;
  }
  if (recordId && feedbackType) {
    return `${CACHE_KEY_PREFIX}${feedbackType}_${recordId}`;
  }
  return null;
}

function getCachedFeedback(cacheKey: string | null): CachedFeedback | null {
  if (!cacheKey) return null;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    const parsed: CachedFeedback = JSON.parse(cached);
    // Check if cache is still valid
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function setCachedFeedback(cacheKey: string | null, feedbackId: string, rating: number): void {
  if (!cacheKey) return;
  try {
    const data: CachedFeedback = {
      feedbackId,
      rating,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(data));
  } catch {
    // localStorage might be full or disabled - fail silently
  }
}

function App() {
  const [rating, setRating] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>("");
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [directParams, setDirectParams] = useState<DirectParams | null>(null);
  const [email, setEmail] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isUnsubscribing, setIsUnsubscribing] = useState<boolean>(false);
  const [action, setAction] = useState<string | null>(null);
  const [showUpdateForm, setShowUpdateForm] = useState<boolean>(false);
  const [submittedRating, setSubmittedRating] = useState<number>(0);
  const [ratingStatus, setRatingStatus] = useState<RatingStatus>('idle');
  const [showCommentForm, setShowCommentForm] = useState<boolean>(false);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);  // Store feedback ID for updates
  const hasSubmittedFromUrl = useRef<boolean>(false);  // Prevent double-submit in StrictMode

  // Helper for API calls
  const postFeedbackData = async (data: FeedbackPayload) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/submit_feedback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );
      
      const responseData = await response.json().catch(() => null);
      if (response.ok) {
         return { success: true, data: responseData };
      } else {
         return { success: false, message: responseData?.message };
      }
    } catch (error) {
      return { success: false, message: "Unable to connect. You can retry or continue adding details." };
    }
  };

  // Auto-submit function for when rating comes from email link or QR code (hybrid approach)
  const submitRatingFromUrl = async (
    ratingParam: number,
    tokenParam: string | null,
    recordIdParam: string | null,
    feedbackTypeParam: string | null,
    emailParam: string | null,
    existingFeedbackId: string | null = null
  ) => {
    setRatingStatus('saving');
    
    const feedbackData: FeedbackPayload = {
      feedbackRating: ratingParam,
      feedbackText: "",
    };

    if (existingFeedbackId) {
      feedbackData.feedbackId = existingFeedbackId;
    }

    if (tokenParam) {
      feedbackData.token = tokenParam;
    } else if (recordIdParam && feedbackTypeParam) {
      feedbackData.recordId = recordIdParam;
      feedbackData.feedbackType = feedbackTypeParam;
      if (emailParam) {
        feedbackData.email = emailParam;
      }
    }

    const { success, data, message } = await postFeedbackData(feedbackData);

    if (success) {
      setSubmittedRating(ratingParam);
      setRatingStatus('saved');
      // Store feedback_id for subsequent updates
      if (data?.feedback_id) {
        setFeedbackId(data.feedback_id);
        // Cache the submission to prevent spam on page refresh
        const cacheKey = getCacheKey(tokenParam, recordIdParam, feedbackTypeParam);
        setCachedFeedback(cacheKey, data.feedback_id, ratingParam);
      }
      // Keep the saved status visible as per user request
    } else {
      setRatingStatus('error');
      setError(message || "Failed to save rating. You can retry or continue adding details.");
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const initialRating = parseInt(params.get("rating") || "0", 10);
    const tokenParam = params.get("token");
    const actionParam = params.get("action");
    
    // Direct URL parameters (for QR codes without token)
    const recordIdParam = params.get("recordId");
    const feedbackTypeParam = params.get("feedbackType");
    const emailParam = params.get("email");

    if (tokenParam) {
      setToken(tokenParam);
    }

    // Set direct params if provided (used when no token)
    // feedbackType can be used alone for category-only feedback
    if (feedbackTypeParam) {
      setDirectParams({
        recordId: recordIdParam || undefined,
        feedbackType: feedbackTypeParam,
      });
    }

    // Pre-fill email if provided in URL
    if (emailParam) {
      setEmail(emailParam);
    }

    if (actionParam) {
      setAction(actionParam);
      if (actionParam === "unsubscribe") {
        if (tokenParam) {
          handleUnsubscribe(tokenParam);
        } else {
          setError("Missing token. Unable to process unsubscribe request.");
        }
        return;
      }
    }

    // Handle rating from URL (from email click or QR code) - use hybrid approach
    if (initialRating > 0 && initialRating <= 5 && !actionParam) {
      setRating(initialRating);
      setShowCommentForm(true);
      
      // Check cache to avoid duplicate submissions on page refresh
      const cacheKey = getCacheKey(tokenParam, recordIdParam, feedbackTypeParam);
      const cached = getCachedFeedback(cacheKey);
      
      if (cached && cached.rating === initialRating) {
        // Same rating already submitted - restore state without API call
        setFeedbackId(cached.feedbackId);
        setSubmittedRating(initialRating);
        setRatingStatus('saved');
        console.log('Feedback restored from cache - skipping duplicate submission');
      } else {
        // New submission or different rating - proceed with API call
        // Use ref to prevent double-submit in React StrictMode
        if (!hasSubmittedFromUrl.current) {
          hasSubmittedFromUrl.current = true;
          setTimeout(() => {
            submitRatingFromUrl(initialRating, tokenParam, recordIdParam, feedbackTypeParam, emailParam, cached?.feedbackId || null);
          }, 100);
        }
      }
    }
  }, []);

  const handleRatingChange = (newRating: number) => {
    const previousRating = rating;
    setRating(newRating);
    setError(null);
    
    // Initial flow: show comment form
    if (!submitted && newRating > 0) {
      setShowCommentForm(true);
    }
    
    // Always background save if rating changed (and not currently saving)
    if (newRating > 0 && newRating !== previousRating && ratingStatus !== 'saving') {
      submitRatingInBackground(newRating);
    }
  };

  // Submit just the rating in background (step 1) - non-blocking
  const submitRatingInBackground = async (ratingValue: number) => {
    setRatingStatus('saving');
    
    const feedbackData: FeedbackPayload = {
      feedbackRating: ratingValue,
      feedbackText: "",
    };
    
    // Include feedbackId if we have one from a previous submission
    if (feedbackId) {
      feedbackData.feedbackId = feedbackId;
    }
    
    if (token) {
      feedbackData.token = token;
    } else if (directParams) {
      if (directParams.recordId) {
        feedbackData.recordId = directParams.recordId;
      }
      feedbackData.feedbackType = directParams.feedbackType;
    }

    const { success, data, message } = await postFeedbackData(feedbackData);

    if (success) {
      setSubmittedRating(ratingValue);
      setRatingStatus('saved');
      // Store feedback_id for subsequent updates
      if (data?.feedback_id) {
        setFeedbackId(data.feedback_id);
        // Cache the submission to prevent spam on page refresh
        const cacheKey = getCacheKey(token, directParams?.recordId || null, directParams?.feedbackType || null);
        setCachedFeedback(cacheKey, data.feedback_id, ratingValue);
      }
      // Keep the saved status visible as per user request
    } else {
      setRatingStatus('error');
      setError(message || "Failed to save rating. You can retry or continue adding details.");
    }
  };

  const retryRating = () => {
    if (rating > 0) {
      setError(null);
      submitRatingInBackground(rating);
    }
  };

  const handleFeedbackChange = (newFeedback: string) => {
    setFeedback(newFeedback);
  };

  const handleEmailChange = (newEmail: string) => {
    setEmail(newEmail);
  };

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      setError("Please select a rating before submitting.");
      return;
    }

    // Validate email if provided
    if (email && !validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    const feedbackData: FeedbackPayload = {
      feedbackRating: rating,
      feedbackText: feedback,
    };
    
    // Include feedbackId if we have one from a previous submission
    if (feedbackId) {
      feedbackData.feedbackId = feedbackId;
    }
    
    if (token) {
      feedbackData.token = token;
    } else if (directParams) {
      if (directParams.recordId) {
        feedbackData.recordId = directParams.recordId;
      }
      feedbackData.feedbackType = directParams.feedbackType;
    }

    // Include email if provided (for follow-up)
    if (email.trim()) {
      feedbackData.email = email.trim();
    }

    const { success, data, message } = await postFeedbackData(feedbackData);

    if (success) {
      setSubmittedRating(rating);
      setSubmitted(true);
      setShowUpdateForm(false);
      setShowCommentForm(false);
      // Store feedback_id for potential updates from the success screen
      if (data?.feedback_id) {
        setFeedbackId(data.feedback_id);
        // Cache the submission to prevent spam on page refresh
        const cacheKey = getCacheKey(token, directParams?.recordId || null, directParams?.feedbackType || null);
        setCachedFeedback(cacheKey, data.feedback_id, rating);
      }
    } else {
      setError(message || "Failed to submit feedback. Please try again.");
    }
    setIsSubmitting(false);
  };

  const handleUnsubscribe = async (token: string) => {
    setIsUnsubscribing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/unsubscribe`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token }),
        }
      );

      if (response.ok) {
        setSubmitted(true);
      } else {
        const errorData = await response.json().catch(() => null);
        setError(errorData?.message || "Failed to unsubscribe. Please try again.");
      }
    } catch (error: any) {
      setError("Unable to connect. Please check your internet connection and try again.");
    }
  };

  return (
    <div className="App">
      <head>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" />
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700&display=swap"
          rel="stylesheet"
        />
      </head>
      <div className="App-header">
        <div className="App-body">
          <div className="logo">
            <Logo />
          </div>
          <div className="App-body-int">
            {error && !showCommentForm && !submitted ? ( // Ensure error doesn't block update form
              <h2>{error}</h2>
            ) : submitted ? (
              <>
                <h2>
                  {isUnsubscribing
                    ? "You have been unsubscribed."
                    : "Thanks for your feedback!"}
                </h2>
                {!isUnsubscribing && submittedRating > 0 && (
                  <>
                    {!showUpdateForm && (
                      <div 
                        className="Update-Link"
                        onClick={() => setShowUpdateForm(true)}
                      >
                        Edit your feedback
                      </div>
                    )}
                    {showUpdateForm && (
                      <>
                        <StarRating rating={rating} onRate={handleRatingChange} />
                        
                        {/* Rating status indicator */}
                        <div className={`Rating-Status ${ratingStatus}`}>
                          {ratingStatus === 'saving' && (
                            <span className="status-saving">Saving rating...</span>
                          )}
                          {ratingStatus === 'saved' && (
                            <span className="status-saved">✓ Rating saved</span>
                          )}
                          {ratingStatus === 'error' && (
                            <span className="status-error">
                              Failed to save rating. <span className="retry-link" onClick={retryRating}>Retry</span>
                            </span>
                          )}
                        </div>

                        {error && (
                          <div className="Error-Inline">{error}</div>
                        )}

                        <TextInputField
                          label=""
                          placeholder="Share your thoughts about the service..."
                          onChange={handleFeedbackChange}
                          value={feedback}
                        />
                        {!token && (
                          <TextInputField
                            label=""
                            placeholder="Email for follow-up"
                            onChange={handleEmailChange}
                            value={email}
                            type="email"
                          />
                        )}
                        <div className="Buttons">
                          <div
                            className={`Invite-Link ${
                              rating === 0 || isSubmitting ? "disabled" : ""
                            }`}
                            onClick={handleSubmit}
                            style={{ pointerEvents: isSubmitting ? "none" : "auto" }}
                          >
                            Update
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            ) : action === "unsubscribe" ? (
              <h2>Processing your unsubscribe request...</h2>
            ) : showCommentForm ? (
              <>
                <h1>Thanks! Want to share more?</h1>
            
                {error && (
                  <div className="Error-Inline">{error}</div>
                )}

                <StarRating rating={rating} onRate={handleRatingChange} />

                {/* Rating status indicator */}
                <div className={`Rating-Status ${ratingStatus}`}>
                  {ratingStatus === 'saving' && (
                    <span className="status-saving">Saving rating...</span>
                  )}
                  {ratingStatus === 'saved' && (
                    <span className="status-saved">✓ Rating saved</span>
                  )}
                  {ratingStatus === 'error' && (
                    <span className="status-error">
                      Failed to save rating. <span className="retry-link" onClick={retryRating}>Retry</span>
                    </span>
                  )}
                </div>
                
                <TextInputField
                  label=""
                  placeholder="Share your thoughts about the service... "
                  onChange={handleFeedbackChange}
                  value={feedback}
                />
                {!token && (
                  <TextInputField
                    label=""
                    placeholder="Email for follow-up (optional)"
                    onChange={handleEmailChange}
                    value={email}
                    type="email"
                  />
                )}
                <div className="Buttons">
                  <div
                     className={`Invite-Link ${
                      isSubmitting || rating === 0 ? "disabled" : ""
                    }`}
                    onClick={() => {
                        if (!isSubmitting && rating > 0) {
                            handleSubmit();
                        }
                    }}
                    style={{ 
                        pointerEvents: isSubmitting || rating === 0 ? "none" : "auto" 
                    }}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </div>
                </div>
              </>
            ) : (
              <>
                <h1>We value your feedback</h1>
                <div className="Text">
                  Rate your experience. Your insights help us improve.
                </div>
                <StarRating rating={rating} onRate={handleRatingChange} />
              </>
            )}
          </div>
          <div className="Links">
            <a
              href="https://realworld-one.com/imprint/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Imprint
            </a>
            <a
              href="https://realworld-one.com/privacy-notice/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Notice
            </a>
            <a
              href="https://support.realworld-one.com/hc/en-us"
              target="_blank"
              rel="noopener noreferrer"
            >
              Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
